/**
 * Inspection data form section (Datos de la Inspección)
 */
import React, { useRef } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar, Upload, Trash2, FileText, Loader2 } from 'lucide-react';

const DatosInspeccionForm = ({ 
  datosInspeccion, 
  onDatosChange,
  onActaUpload,
  onActaDelete,
  actaInputRef,
  inspeccionId,
  readOnly = false,
  loading = false,
  sendingEmail = false
}) => {
  const localRef = useRef(null);
  const inputRef = actaInputRef || localRef;
  
  // Check if email was sent (any of these states means email was sent)
  const emailWasSent = ['enviado_correo', 'aprobado_correo', 'rechazado_correo', 'enviado_whatsapp', 'aprobado_whatsapp', 'rechazado_whatsapp'].includes(datosInspeccion.actaEstado);
  
  // Generate acta view URL dynamically
  const actaViewUrl = inspeccionId 
    ? `${process.env.REACT_APP_BACKEND_URL}/api/inspecciones/${inspeccionId}/acta-view`
    : null;

  return (
    <Card className="border-[#E8E8E7]">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <Calendar size={18} className="text-[#9d8272]" />
          Datos de la Inspección
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Fecha *</Label>
            <Input
              type="date"
              value={datosInspeccion.fecha}
              onChange={(e) => onDatosChange({...datosInspeccion, fecha: e.target.value})}
              className={`border-[#c5b8a2] text-sm ${readOnly ? 'bg-gray-50' : ''}`}
              readOnly={readOnly}
              data-testid="inspeccion-fecha"
            />
          </div>
          <div>
            <Label className="text-xs">Hora Inicio *</Label>
            <Input
              type="time"
              value={datosInspeccion.horaInicio}
              onChange={(e) => onDatosChange({...datosInspeccion, horaInicio: e.target.value})}
              className={`border-[#c5b8a2] text-sm ${readOnly ? 'bg-gray-50' : ''}`}
              readOnly={readOnly}
              data-testid="inspeccion-hora-inicio"
            />
          </div>
          <div>
            <Label className="text-xs">Hora Fin</Label>
            <Input
              type="time"
              value={datosInspeccion.horaFin}
              readOnly
              className="border-[#c5b8a2] text-sm bg-gray-50"
              placeholder="Auto al terminar"
              data-testid="inspeccion-hora-fin"
            />
          </div>
          <div>
            <Label className="text-xs">Acta de Inspección</Label>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              onChange={onActaUpload}
              className="hidden"
            />
            
            {/* Show spinner while sending email */}
            {sendingEmail ? (
              <div className="flex items-center gap-2 mt-1 text-blue-600">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Enviando correo...</span>
              </div>
            ) : emailWasSent && actaViewUrl ? (
              // Show link to acta when email was sent
              <div className="flex flex-col gap-1 mt-1">
                <a
                  href={actaViewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-sm"
                  data-testid="acta-link"
                >
                  <FileText size={14} />
                  <span>Ver Acta de Inspección</span>
                </a>
                <Badge 
                  className={`text-[10px] w-fit ${
                    datosInspeccion.actaEstado === 'aprobado_correo' || datosInspeccion.actaEstado === 'aprobado_whatsapp' ? 'bg-green-100 text-green-800 border-green-300' :
                    datosInspeccion.actaEstado === 'rechazado_correo' || datosInspeccion.actaEstado === 'rechazado_whatsapp' ? 'bg-red-100 text-red-800 border-red-300' :
                    datosInspeccion.actaEstado === 'enviado_whatsapp' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                    'bg-blue-100 text-blue-800 border-blue-300'
                  }`} 
                  variant="outline"
                >
                  {datosInspeccion.actaEstado === 'enviado_correo' ? 'Enviado por correo' : 
                   datosInspeccion.actaEstado === 'aprobado_correo' ? 'Aprobado por correo' :
                   datosInspeccion.actaEstado === 'rechazado_correo' ? 'Rechazado por correo' :
                   datosInspeccion.actaEstado === 'enviado_whatsapp' ? 'Enviado por WhatsApp' :
                   datosInspeccion.actaEstado === 'aprobado_whatsapp' ? 'Aprobado (WhatsApp)' :
                   datosInspeccion.actaEstado === 'rechazado_whatsapp' ? 'Rechazado (WhatsApp)' :
                   datosInspeccion.actaEstado}
                </Badge>
              </div>
            ) : datosInspeccion.actaUrl ? (
              // Show link when acta was uploaded manually
              <div className="flex items-center gap-2 mt-1">
                <a
                  href={datosInspeccion.actaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-sm"
                  data-testid="acta-link"
                >
                  <FileText size={14} />
                  <span>Acta de inspección</span>
                </a>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onActaDelete}
                    disabled={loading}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                    title="Eliminar acta"
                    data-testid="acta-delete-btn"
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            ) : !readOnly ? (
              // Show upload button when no acta and email not sent (only in edit mode)
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => inputRef.current?.click()}
                  disabled={loading}
                  className="border-[#c5b8a2] text-[#9d8272] flex-1"
                  data-testid="acta-upload-btn"
                >
                  <Upload size={14} className="mr-1" />
                  Adjuntar
                </Button>
              </div>
            ) : (
              <span className="text-xs text-gray-500 mt-1 block">Sin acta</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DatosInspeccionForm;
